// @ts-check
import { useState } from 'react';
import _ from 'lodash';
import { Flex, Padding } from 'components/base/layout-components';
import Text from 'components/base/Text';
import RepositoriesAutocomplete from './RepositoriesAutocomplete';
import usePaths from './usePaths';
import PathsViewer from './PathsViewer';
import * as S from './ServiceForm.styles';
import useServiceFormValidation from './useServiceFormValidation';
import useSelectedRepo from './useSelectedRepo';
import Icon from 'components/base/Icon';
import { LBButton } from '@linearb/buzz';

const ServiceForm = ({ service, onSubmit, onCancel }) => {
  const [serviceName, setServiceName] = useState(_.get(service, 'name', ''));
  const [selectedRepo, setSelectedRepo] = useSelectedRepo(service);
  const { paths, insertFolder, editFolder, removeFolder } = usePaths(
    _.get(service, 'settings.path_strategy')
  );
  const [inputPath, setInputPath] = useState('');
  const formValidation = useServiceFormValidation({
    serviceName,
    selectedRepo,
    paths
  });

  const serviceId = _.get(service, 'id');
  const isEditMode = !!serviceId;
  const selectedRepoId = _.get(selectedRepo, 'id');

  const onEditExistedFolder = (repoId, oldFolder, newFolder) => {
    if (oldFolder === newFolder) return;
    const parsedPath = formValidation.parsePath(newFolder);
    if (!formValidation.validatePath(parsedPath, repoId)) return;
    editFolder(repoId, oldFolder, parsedPath);
  };

  const onPathInputSubmit = () => {
    const parsedPath = formValidation.parsePath(inputPath);
    if (!formValidation.validatePath(parsedPath, selectedRepoId)) return;

    insertFolder(selectedRepoId, parsedPath);
    setInputPath('');
  };

  const beforeSubmitForm = e => {
    e.preventDefault();
    if (!formValidation.validateForm()) return;

    const servicePayload = {
      id: serviceId,
      name: serviceName,
      type: 'preconfigured',
      settings: {
        path_strategy: paths
      }
    };
    onSubmit(servicePayload);
  };

  return (
    <Padding padding='medium' data-cy='add-service-form'>
      <Flex direction='column'>
        <Text size='x-x-large' wieght='400'>
          {isEditMode ? `Edit` : 'Add'} Service
        </Text>
        <Text size='x-medium' mt='1rem'>
          Specify paths to Git folders to monitor as services <br />
          You may add multiple repositories, and multiple paths per repository.
        </Text>
      </Flex>
      <S.ServiceForm onSubmit={beforeSubmitForm}>
        <S.Row>
          <S.Field>
            <label>SERVICE NAME</label>
            <S.Input
              value={serviceName}
              onChange={e => setServiceName(e.target.value)}
              placeholder='Service name...'
              autoFocus
              data-cy='input-service-name'
            />
          </S.Field>
          <S.Field>
            <label>REPOSITORY</label>
            <RepositoriesAutocomplete
              selectedRepo={selectedRepo}
              onChange={setSelectedRepo}
            />
          </S.Field>
        </S.Row>
        <S.Row>
          <S.Field className='path'>
            <label>PATH</label>
            <main>
              <S.Input
                value={inputPath}
                onChange={e => setInputPath(e.target.value)}
                placeholder='e.g. folder1/subfolder'
                data-cy='input-path'
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    onPathInputSubmit();
                  }
                }}
              />
              <LBButton
                variant='outline'
                size='icon'
                type='button'
                onClick={onPathInputSubmit}
                tabIndex={-1}
                data-cy='add-path'
              >
                <Icon name='Add' />
              </LBButton>
            </main>
            <div className='input-description'>
              Use slash (/) to specify entire repository as a service, or part
              of a service.
            </div>
          </S.Field>
        </S.Row>
        <S.Row>
          <PathsViewer
            paths={paths}
            selectedRepo={selectedRepo}
            onRemoveValue={removeFolder}
            onEditValue={onEditExistedFolder}
            onRepoHeaderClick={repo => setSelectedRepo(repo)}
          />
        </S.Row>
        <footer>
          <LBButton
            variant='outline'
            size='lg'
            onClick={onCancel}
            data-cy='ServiceForm-btn-cancel'
          >
            Cancel
          </LBButton>
          <LBButton type='submit' size='lg' data-cy='ServiceForm-btn-submit'>
            Save
          </LBButton>
        </footer>
      </S.ServiceForm>
    </Padding>
  );
};

export default ServiceForm;
